import { useState } from "react";
import { Check, X, ChevronDown, ChevronUp, ListChecks } from "lucide-react";

import { Button } from "@/components/ui/button";
import { getCategory } from "@/lib/categories";
import { applyProposal } from "@/lib/applyProposals";
import { updateProposalBatch } from "@/lib/proposals";
import { pad2 } from "@/lib/time";
import { useTranslation } from "@/i18n";
import { toast } from "sonner";

const TASK_KINDS = new Set(["task_create", "task_edit", "task_delete", "task_complete", "task_skip"]);

const KNOWN_KINDS = new Set([
  "create", "edit", "delete", "move", "extend", "shorten", "mark",
  "task_create", "task_edit", "task_delete", "task_complete", "task_skip",
]);

function fmtTime(h, m) {
  if (h == null || m == null) return "—";
  return `${pad2(h)}:${pad2(m)}`;
}

function summarizeTask(p, t) {
  const n = p.new || {};
  const o = p.old || {};
  const label = n.title || o.title || t("proposal.taskFallback");
  if (p.kind === "task_create" && n.targetDate) return `${label} · ${n.targetDate}`;
  return `${label}`;
}

function summarize(p, t) {
  if (TASK_KINDS.has(p.kind)) return summarizeTask(p, t);
  const newV = p.new || {};
  const oldV = p.old || {};
  const evt = t("proposal.eventFallback");
  if (p.kind === "delete") {
    return `${t("proposal.deletePrefix")}: ${oldV.title || evt}`;
  }
  if (p.kind === "mark") {
    return `${oldV.title || evt} → ${newV.status || "—"}`;
  }
  if (p.kind === "create") {
    return `${t("proposal.createPrefix")}: ${newV.title || evt} ${newV.date || ""} ${fmtTime(newV.startHour, newV.startMinute)}–${fmtTime(newV.endHour, newV.endMinute)}`;
  }
  return `${oldV.title || newV.title || evt}: ${fmtTime(oldV.startHour, oldV.startMinute)}–${fmtTime(oldV.endHour, oldV.endMinute)} → ${fmtTime(newV.startHour, newV.startMinute)}–${fmtTime(newV.endHour, newV.endMinute)}`;
}

export default function ProposalCard({ batch, onApplied, onRejected }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [selected, setSelected] = useState(
    () => new Set(batch.proposals.map((_, i) => i))
  );

  const allSelected = selected.size === batch.proposals.length;

  const toggle = (i) => {
    const next = new Set(selected);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    setSelected(next);
  };

  const approveSelected = () => {
    const idxs = [...selected].sort((a, b) => a - b);
    const remaining = [];
    let okCount = 0;
    let failCount = 0;
    batch.proposals.forEach((p, i) => {
      if (idxs.includes(i)) {
        const res = applyProposal(p);
        if (res.ok) okCount += 1;
        else {
          failCount += 1;
          remaining.push(p);
        }
      } else {
        remaining.push(p);
      }
    });
    if (failCount > 0) toast.error(t("proposal.applyFailed", { count: failCount }));
    if (remaining.length === 0) {
      onApplied?.(okCount);
    } else {
      updateProposalBatch(batch.id, (b) => ({ ...b, proposals: remaining }));
      setSelected(new Set(remaining.map((_, i) => i)));
    }
  };

  const approveAll = () => {
    const remaining = [];
    let okCount = 0;
    let failCount = 0;
    batch.proposals.forEach((p) => {
      const res = applyProposal(p);
      if (res.ok) okCount += 1;
      else {
        failCount += 1;
        remaining.push(p);
      }
    });
    if (failCount > 0) {
      toast.error(t("proposal.applyFailed", { count: failCount }));
      updateProposalBatch(batch.id, (b) => ({ ...b, proposals: remaining }));
      setSelected(new Set(remaining.map((_, i) => i)));
    } else {
      onApplied?.(okCount);
    }
  };

  const itemCount = batch.proposals.length;
  const itemLabel = itemCount === 1 ? t("proposal.itemOne", { count: itemCount }) : t("proposal.itemMany", { count: itemCount });

  return (
    <div
      className="rounded-2xl border border-border bg-card overflow-hidden"
      data-testid={`proposal-card-${batch.id}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition"
        data-testid={`proposal-toggle-${batch.id}`}
      >
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
            {batch.mode === "calendar" ? t("proposal.calendarChanges") : t("proposal.updates")} · {itemLabel}
          </p>
          <p className="text-sm text-slate-900 font-medium mt-0.5 truncate">
            {batch.summary || t("proposal.reviewApprove")}
          </p>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
      </button>

      {open ? (
        <div className="border-t border-border">
          <ul className="divide-y divide-border" data-testid={`proposal-list-${batch.id}`}>
            {batch.proposals.map((p, i) => {
              const isTask = TASK_KINDS.has(p.kind);
              const cat = getCategory((p.new && p.new.category) || (p.old && p.old.category) || "free_time");
              const checked = selected.has(i);
              const itemKey = `${p.kind}:${(p.new && p.new.title) || (p.old && p.old.title) || ""}:${(p.new && p.new.date) || (p.old && p.old.date) || ""}:${(p.new && p.new.startHour) ?? (p.old && p.old.startHour) ?? ""}`;
              return (
                <li
                  key={itemKey}
                  className="px-4 py-3 flex items-start gap-3"
                  data-testid={`proposal-item-${batch.id}-${i}`}
                >
                  <button
                    onClick={() => toggle(i)}
                    className={[
                      "mt-0.5 h-5 w-5 rounded-md border flex items-center justify-center shrink-0",
                      checked ? "bg-slate-900 border-slate-900 text-white" : "border-slate-300 bg-white",
                    ].join(" ")}
                    data-testid={`proposal-check-${batch.id}-${i}`}
                  >
                    {checked ? <Check className="h-3 w-3" /> : null}
                  </button>

                  {isTask ? (
                    <span
                      className="h-7 w-7 rounded-md border border-black/5 shrink-0 mt-0.5 bg-slate-100 flex items-center justify-center"
                      aria-hidden
                    >
                      <ListChecks className="h-4 w-4 text-slate-600" />
                    </span>
                  ) : (
                    <span
                      className="h-7 w-7 rounded-md border border-black/5 shrink-0 mt-0.5"
                      style={{ backgroundColor: cat.hex }}
                      aria-hidden
                    />
                  )}

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wider px-2 py-0.5">
                        {KNOWN_KINDS.has(p.kind) ? t(`proposal.kinds.${p.kind}`) : t("proposal.updates")}
                      </span>
                      <span className="text-sm font-medium text-slate-900 truncate">
                        {summarize(p, t)}
                      </span>
                    </div>
                    {p.reason ? (
                      <p className="text-xs text-slate-600 mt-1">{p.reason}</p>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="px-4 py-3 flex flex-wrap items-center gap-2 bg-slate-50/60 border-t border-border">
            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={() => setSelected(new Set(batch.proposals.map((_, i) => i)))}
              data-testid={`proposal-select-all-${batch.id}`}
            >
              {t("proposal.selectAll")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="rounded-full text-slate-600"
              onClick={() => setSelected(new Set())}
              data-testid={`proposal-clear-${batch.id}`}
            >
              {t("proposal.clear")}
            </Button>
            <span className="flex-1" />
            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={() => onRejected?.()}
              data-testid={`proposal-reject-${batch.id}`}
            >
              <X className="h-3.5 w-3.5 mr-1" /> {t("proposal.reject")}
            </Button>
            <Button
              size="sm"
              className="rounded-full bg-slate-900 hover:bg-slate-800"
              onClick={allSelected ? approveAll : approveSelected}
              disabled={selected.size === 0}
              data-testid={`proposal-approve-${batch.id}`}
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              {allSelected ? t("proposal.approveAll") : t("proposal.approveN", { count: selected.size })}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
